/**
 * Serviço de API - Organizações (Multi-Tenant)
 *
 * CONTEXTO:
 * Encapsula chamadas HTTP relacionadas ao gerenciamento de organizações,
 * membros e convites. Reutiliza a instância `clienteApi` para garantir
 * configuração centralizada e autenticação via token.
 *
 * RESPONSABILIDADES:
 * - CRUD de organizações
 * - Gestão de membros (listar, alterar role, remover)
 * - Sistema de convites (criar, listar, cancelar, aceitar)
 *
 * PADRÕES:
 * - Tipos importados de `../tipos/tiposOrganizacao`
 * - Erros do axios são transformados em mensagens amigáveis em português
 * - Documentação exhaustiva (AI_MANUAL_DE_MANUTENCAO.md)
 */

import type { AxiosError } from 'axios';
import { clienteApi } from './clienteApi';
import type {
  Organizacao,
  MembroOrganizacao,
  ConviteOrganizacao,
  DadosCriacaoOrganizacao,
  DadosAtualizacaoOrganizacao,
  DadosEnvioConvite,
  DadosAlteracaoRole,
  DadosTransferenciaOwnership,
  RespostaAceitarConvite,
  RespostaSucesso,
} from '../tipos/tiposOrganizacao';

// ===== Helpers =====

/**
 * Helper interno para mapear erros HTTP do axios para mensagens em português.
 */
function mapearErroParaMensagem(error: AxiosError): string {
  if (error.isAxiosError && error.response) {
    const status = error.response.status;
    const data = error.response.data as { detail?: string; message?: string; mensagem?: string };
    const backendMensagem = data?.detail || data?.message || data?.mensagem;

    if (status === 400) {
      return backendMensagem || 'Dados inválidos: verifique os campos enviados.';
    }
    if (status === 401) {
      return backendMensagem || 'Não autorizado. Faça login novamente.';
    }
    if (status === 403) {
      return backendMensagem || 'Você não tem permissão para esta operação.';
    }
    if (status === 404) {
      return backendMensagem || 'Recurso não encontrado.';
    }
    if (status === 409) {
      return backendMensagem || 'Conflito: recurso já existe.';
    }
    if (status >= 500) {
      return backendMensagem || 'Erro no servidor. Tente novamente mais tarde.';
    }
    return backendMensagem || `Erro HTTP ${status}`;
  }

  if (error && error.message) {
    return error.message;
  }

  return 'Erro desconhecido ao comunicar com o servidor.';
}

// ===== CRUD de Organizações =====

/**
 * Cria uma nova organização.
 * O usuário autenticado se torna automaticamente o owner.
 */
export async function criarOrganizacao(dados: DadosCriacaoOrganizacao): Promise<Organizacao> {
  try {
    const response = await clienteApi.post<Organizacao>('/api/core/organizacoes', dados);
    return response.data;
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

/**
 * Lista todas as organizações das quais o usuário é membro.
 */
export async function listarMinhasOrganizacoes(): Promise<Organizacao[]> {
  try {
    const response = await clienteApi.get<Organizacao[]>('/api/core/organizacoes');
    return response.data;
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

/**
 * Obtém detalhes de uma organização específica.
 */
export async function obterOrganizacao(organizacaoId: string): Promise<Organizacao> {
  try {
    const response = await clienteApi.get<Organizacao>(`/api/core/organizacoes/${organizacaoId}`);
    return response.data;
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

/**
 * Atualiza dados de uma organização.
 * Requer permissão de admin ou owner.
 */
export async function atualizarOrganizacao(
  organizacaoId: string,
  dados: DadosAtualizacaoOrganizacao
): Promise<Organizacao> {
  try {
    const response = await clienteApi.put<Organizacao>(
      `/api/core/organizacoes/${organizacaoId}`,
      dados
    );
    return response.data;
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

/**
 * Desativa uma organização (soft delete).
 * Apenas o owner pode realizar esta operação.
 */
export async function desativarOrganizacao(organizacaoId: string): Promise<void> {
  try {
    await clienteApi.delete(`/api/core/organizacoes/${organizacaoId}`);
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

// ===== Gestão de Membros =====

/**
 * Lista todos os membros de uma organização.
 */
export async function listarMembros(organizacaoId: string): Promise<MembroOrganizacao[]> {
  try {
    const response = await clienteApi.get<MembroOrganizacao[]>(
      `/api/core/organizacoes/${organizacaoId}/membros`
    );
    return response.data;
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

/**
 * Altera o role de um membro na organização.
 * Owner pode alterar qualquer role (exceto o próprio).
 * Admin pode alterar apenas members.
 */
export async function alterarRoleMembro(
  organizacaoId: string,
  usuarioId: string,
  dados: DadosAlteracaoRole
): Promise<RespostaSucesso> {
  try {
    const response = await clienteApi.put<RespostaSucesso>(
      `/api/core/organizacoes/${organizacaoId}/membros/${usuarioId}`,
      dados
    );
    return response.data;
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

/**
 * Remove um membro da organização.
 * Owner pode remover qualquer membro (exceto ele mesmo).
 * Admin pode remover apenas members.
 */
export async function removerMembro(
  organizacaoId: string,
  usuarioId: string
): Promise<void> {
  try {
    await clienteApi.delete(`/api/core/organizacoes/${organizacaoId}/membros/${usuarioId}`);
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

/**
 * Sai da organização (remove o próprio usuário).
 * O owner não pode sair - deve transferir a propriedade primeiro.
 */
export async function sairDaOrganizacao(organizacaoId: string): Promise<void> {
  try {
    await clienteApi.post(`/api/core/organizacoes/${organizacaoId}/sair`);
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

/**
 * Transfere a propriedade da organização para outro membro.
 * Apenas o owner atual pode realizar esta operação.
 */
export async function transferirOwnership(
  organizacaoId: string,
  dados: DadosTransferenciaOwnership
): Promise<RespostaSucesso> {
  try {
    const response = await clienteApi.post<RespostaSucesso>(
      `/api/core/organizacoes/${organizacaoId}/transferir`,
      dados
    );
    return response.data;
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

// ===== Sistema de Convites =====

/**
 * Cria um convite para um email entrar na organização.
 * Requer permissão de admin ou owner.
 */
export async function criarConvite(
  organizacaoId: string,
  dados: DadosEnvioConvite
): Promise<ConviteOrganizacao> {
  try {
    const response = await clienteApi.post<ConviteOrganizacao>(
      `/api/core/organizacoes/${organizacaoId}/convites`,
      dados
    );
    return response.data;
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

/**
 * Lista convites pendentes de uma organização.
 * Requer permissão de admin ou owner.
 */
export async function listarConvitesPendentes(
  organizacaoId: string
): Promise<ConviteOrganizacao[]> {
  try {
    const response = await clienteApi.get<ConviteOrganizacao[]>(
      `/api/core/organizacoes/${organizacaoId}/convites`
    );
    return response.data;
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

/**
 * Cancela um convite pendente.
 * Requer permissão de admin ou owner.
 */
export async function cancelarConvite(
  organizacaoId: string,
  conviteId: string
): Promise<void> {
  try {
    await clienteApi.delete(`/api/core/organizacoes/${organizacaoId}/convites/${conviteId}`);
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

/**
 * Obtém informações de um convite pelo token.
 * Este endpoint é público (não requer autenticação).
 */
export async function obterConvitePorToken(token: string): Promise<ConviteOrganizacao> {
  try {
    const response = await clienteApi.get<ConviteOrganizacao>(`/api/core/convites/${token}`);
    return response.data;
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

/**
 * Aceita um convite e adiciona o usuário à organização.
 * Requer autenticação.
 */
export async function aceitarConvite(token: string): Promise<RespostaAceitarConvite> {
  try {
    const response = await clienteApi.post<RespostaAceitarConvite>(
      `/api/core/convites/${token}/aceitar`
    );
    return response.data;
  } catch (error) {
    throw new Error(mapearErroParaMensagem(error as AxiosError));
  }
}

// ===== Export do serviço como objeto =====

export const servicoOrganizacao = {
  // Organizações
  criarOrganizacao,
  listarMinhasOrganizacoes,
  obterOrganizacao,
  atualizarOrganizacao,
  desativarOrganizacao,
  // Membros
  listarMembros,
  alterarRoleMembro,
  removerMembro,
  sairDaOrganizacao,
  transferirOwnership,
  // Convites
  criarConvite,
  listarConvitesPendentes,
  cancelarConvite,
  obterConvitePorToken,
  aceitarConvite,
};

export default servicoOrganizacao;
